import * as React from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import useForm from 'antd/lib/form/hooks/useForm';
import Input from 'antd/lib/input';
import { tryUpdateOrgSettings, UpdateOrgUserVCSUserNameRequest } from '@/actions/orgSettings';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { displayUserNameLong } from '@/helpers/displayHelpers';
import { IntegrationProvider, IntegrationProviderId, OrgUser } from '@/types';
import { globalMessage } from '@/utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const vcsUserNamesFieldName = 'vcsUserNames';

export const vcsUserNameUpdatableId = (userId: string) => `vcsUserNames-${userId}`;

// Providers of the same VCS share a name, so the id is spelled out only when it's what tells them apart.
export const vcsUserNameLabel = (vcsProvider: IntegrationProvider, vcsProviders: IntegrationProvider[]) =>
    vcsProviders.filter((provider) => provider.name === vcsProvider.name).length > 1 ?
        `${vcsProvider.name} username (${vcsProvider.id})`
    :   `${vcsProvider.name} username`;

interface EditVCSUserNamesModalProps {
    open: boolean;
    orgId: string;
    user: OrgUser | null;
    vcsProviders: IntegrationProvider[];
    vcsUserNamesByProviderId: Record<IntegrationProviderId, string>;
    onCancel: () => void;
    onSaved: () => void;
}

const EditVCSUserNamesModal: React.FC<EditVCSUserNamesModalProps> = (props: EditVCSUserNamesModalProps) => {
    const dispatch = useDispatch();
    const [form] = useForm();
    const { user, onCancel, onSaved } = props;
    const updatableId = vcsUserNameUpdatableId(user?.id ?? '');
    const isSaving = useIsLoading(OrgSettingsUpdatable.UpdateVCSUserNameForOrgUser, updatableId);
    const saveSucceeded = useUpdated(OrgSettingsUpdatable.UpdateVCSUserNameForOrgUser, updatableId);
    const saveError = useError(OrgSettingsUpdatable.UpdateVCSUserNameForOrgUser, updatableId);

    React.useEffect(() => {
        if (saveSucceeded) {
            globalMessage.success('VCS usernames updated successfully');
            onSaved();
        }
    }, [saveSucceeded, onSaved]);

    React.useEffect(() => {
        if (saveError) {
            // stays open so the rejected username can be corrected or dropped
            globalMessage.error(saveError);
        }
    }, [saveError]);

    const saveUserNames = (values: { vcsUserNames?: Record<IntegrationProviderId, string> }) => {
        if (!user) {
            return;
        }

        const changedUserNames: Record<IntegrationProviderId, string> = {};
        props.vcsProviders.forEach((vcsProvider) => {
            const value = values.vcsUserNames?.[vcsProvider.id];
            if (value === undefined || value.trim() === (props.vcsUserNamesByProviderId[vcsProvider.id] ?? '')) {
                return;
            }

            changedUserNames[vcsProvider.id] = value.trim();
        });

        if (Object.keys(changedUserNames).length === 0) {
            onCancel();
            return;
        }

        const request: UpdateOrgUserVCSUserNameRequest = {
            orgId: props.orgId,
            userId: user.email,
            vcsUserNames: changedUserNames,
        };
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateVCSUserNameForOrgUser, request, updatableId));
    };

    return (
        <Modal
            open={props.open}
            title={user ? `VCS usernames for '${displayUserNameLong(user)}'` : 'VCS usernames'}
            okText="Save"
            onOk={form.submit}
            okButtonProps={{ loading: isSaving }}
            onCancel={onCancel}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={saveUserNames}
                preserve={false}
                initialValues={{ [vcsUserNamesFieldName]: props.vcsUserNamesByProviderId }}
            >
                {props.vcsProviders.map((vcsProvider) => (
                    <FormItem
                        key={vcsProvider.id}
                        label={vcsUserNameLabel(vcsProvider, props.vcsProviders)}
                        name={[vcsUserNamesFieldName, vcsProvider.id]}
                        extra="Leave empty to not associate any username with this user."
                    >
                        <Input placeholder={`${vcsProvider.name} username`} allowClear />
                    </FormItem>
                ))}
            </Form>
        </Modal>
    );
};

export default EditVCSUserNamesModal;
